import { Form, Input, Button, Card, Checkbox, Alert } from 'antd';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginAPI } from '../api/auth.api';
import { useAuth } from '../context/AuthContext';

const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

export default function Login() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [forgotEmail, setForgotEmail] = useState('');

  // Pre-fill email if remembered
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (saved) {
      form.setFieldsValue({ email: saved, rememberMe: true });
    }
  }, []);

  const onFinish = async (values: any) => {
    setLoading(true);
    setLoginStatus(null);
    try {
      const result = await loginAPI(values.email, values.password);
      login(result.token, result.user);

      if (values.rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, values.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      setLoginStatus({ type: 'success', msg: 'Login successful! Redirecting...' });
      setTimeout(() => {
        if (result.user.type === 'admin' || result.user.type === 'superadmin') {
          navigate('/admin');
        } else {
          navigate('/orders');
        }
      }, 1000);
    } catch (error: any) {
      // Backend returns { error: { code, message } } — pluck message so we
      // don't hand React an object to render (crashes with React error #31).
      const apiErr = error.response?.data?.error;
      const msg =
        (typeof apiErr === 'string' && apiErr) ||
        apiErr?.message ||
        error.response?.data?.message ||
        'Login failed. Please check your credentials.';
      setLoginStatus({ type: 'error', msg });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentEmail = form.getFieldValue('email') || '';
    setForgotEmail(currentEmail);
    // navigate to forgot page
    window.location.href = '/forgot';
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      flexDirection: 'column'
    }}>
      <div>
        <img
          src="/images/logo.png"
          alt="Logo"
          style={{ height: 80, marginBottom: 24 }}
        />
      </div>
      <Card
        style={{ width: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        title={<h2 style={{ textAlign: 'center', margin: 0 }}>Admin Login</h2>}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="admin@example.com" type="email" size="large" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password placeholder="Enter password" size="large" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Form.Item name="rememberMe" valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox>Remember me</Checkbox>
            </Form.Item>
            <a href="#" onClick={handleForgotOpen}>Forgot Password?</a>
          </div>

          {loginStatus && (
            <Form.Item>
              <Alert type={loginStatus.type} message={loginStatus.msg} showIcon />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
            >
              Login
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Forgot password handled on separate page */}
    </div>
  );
}
